import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useMemo } from 'react';
import cn from 'classnames';
import { useIntentBehavior } from '../../providers/IntentBehavior';
import { getTestIds } from '../../utils/TestIds';
import { useBiAction } from '../BiTracking';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { Tooltip } from '../Tooltip';
import styles from './IconButton.module.scss';
const BORDER_SKIN_CLASS = {
    default: undefined,
    subtle: styles.subtleBorderSkin
};
export const GetIconButtonTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            IconButtonTID: {
                IconButton: (testid)=>testid !== null && testid !== void 0 ? testid : '',
                TooltipContent: (testid)=>testid ? "".concat(testid, "-wrapper") : ''
            }
        }
    });
export const IconButton = /*#__PURE__*/ React.forwardRef(function IconButton(props, ref) {
    const { className, children, disabled, size = 'medium', skin = 'standard', aspect = 'round', noPadding, cursorDefault, intent, pressed, tooltip, bordered, borderSkin, preferIntentTooltip, testId, bi, noBi: _noBi, onClick } = props, rest = _object_without_properties(props, [
        "className",
        "children",
        "disabled",
        "size",
        "skin",
        "aspect",
        "noPadding",
        "cursorDefault",
        "intent",
        "pressed",
        "tooltip",
        "bordered",
        "borderSkin",
        "preferIntentTooltip",
        "testId",
        "bi",
        "noBi",
        "onClick"
    ]);
    const { getDisabled, getTooltip } = useIntentBehavior();
    const disabledFromIntent = intent ? getDisabled(intent) : false;
    const tooltipFromIntent = intent ? getTooltip(intent) : null;
    const hasTooltip = !!(tooltip === null || tooltip === void 0 ? void 0 : tooltip.title) || !!tooltipFromIntent;
    const { IconButtonTID } = useMemo(()=>GetIconButtonTID(testId !== null && testId !== void 0 ? testId : ''), [
        testId
    ]);
    const logBiAction = useBiAction();
    const handleClick = useCallback((event)=>{
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'icon_button',
                entityName: bi.name,
                actionType: 'click',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : ''
                })
            });
        }
        return onClick === null || onClick === void 0 ? void 0 : onClick(event);
    }, [
        bi,
        logBiAction,
        onClick,
        testId
    ]);
    return /*#__PURE__*/ _jsx(ConditionalWrapper, {
        condition: hasTooltip,
        wrapper: (children)=>/*#__PURE__*/ _jsx(Tooltip, _object_spread_props(_object_spread({}, preferIntentTooltip && tooltipFromIntent ? {
                placement: 'top',
                title: tooltipFromIntent
            } : tooltip || {
                placement: 'top',
                title: tooltipFromIntent
            }), {
                children: /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.wrapper, className),
                    "data-testid": IconButtonTID.TooltipContent(testId),
                    children: children
                })
            })),
        children: /*#__PURE__*/ _jsx("button", _object_spread_props(_object_spread({
            ref: ref,
            disabled: disabledFromIntent || disabled,
            className: cn(styles.root, styles["".concat(skin, "Skin")], styles["".concat(size, "Size")], {
                [styles.squareAspect]: aspect === 'square',
                [styles.noPadding]: noPadding,
                [styles.cursorDefault]: cursorDefault,
                [styles.bordered]: bordered,
                [styles.pressed]: pressed
            }, borderSkin != null && BORDER_SKIN_CLASS[borderSkin], !hasTooltip && className),
            onClick: handleClick
        }, pressed != null ? {
            'aria-pressed': pressed
        } : {}, rest, testId != null ? {
            'data-testid': testId
        } : {}), {
            children: children
        }))
    });
});
